import {DateTime} from 'luxon';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {TeammateAvatar} from '../../components/common/avatars/teammateAvatar';
import {buildTeammateDisplayName} from '../../helpers/modelHelpers';
import {TeammateAccountStatusesEnum, TeammateModel} from '../../models/api/teammateModel';
import {settingsNavigationWidth} from '../settingsCommonConstants';
import {SettingsHomeShortcutIcon} from './settingsHomeShortcutIcon';

/*
 * Props.
 */

export interface SettingsHomePendingTeammateProps {
  teammate: TeammateModel;
  onClick: Handler;
}

interface SettingsHomePendingTeammatesProps {
  onPrimaryClick: Handler;
  primaryLabel: ReactNode;
  onSecondaryClick: Handler;
  secondaryLabel: ReactNode;
  title: ReactNode;
  icon: IconName;
  teammates: ReadonlyArray<SettingsHomePendingTeammateProps>;
  isGlobalAdmin: boolean;
}

/*
 * Style.
 */

const StyledShortcutDiv = styled.div`
  border-radius: 16px;
  background: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0 1px 9px ${p.theme.alphas.black20}`)};
  border: 1px solid ${(p) => p.theme.alphas.black20};

  display: grid;
  grid-template-areas:
    'icon . title'
    'header header header'
    'content content content'
    'footer footer footer';
  grid-template-columns: auto 16px 1fr;
  grid-template-rows: auto auto 1fr auto;

  min-width: 300px;
  overflow: hidden;

  grid-column: 1 / 2;

  @media (min-width: ${970 + settingsNavigationWidth}px) {
    grid-column: 1 / 3;
  }

  @media (min-width: ${1200 + settingsNavigationWidth}px) {
    grid-column: 3 / 7;
  }
`;

const IconWrapper = styled.div`
  grid-area: icon;
  padding-top: 20px;
  padding-left: 20px;
`;

const StyledShortcutTitle = styled.h3`
  margin: 0;
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
`;

const StyledTitleWrapper = styled.div`
  grid-area: title;
  padding-top: 31px;

  display: flex;
  flex-direction: column;
`;

const StyledTeammateNameAndAvatar = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  gap: 5px;
`;

const StyledOverflowTextWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTextWrapper = styled.div`
  flex-grow: 1;
`;

const StyledContentWrapper = styled.div`
  grid-area: content;
  height: 195px;
  overflow-y: scroll;

  @media (max-width: ${1200 + settingsNavigationWidth}px) {
    height: 176px;
  }

  @media (max-width: ${970 + settingsNavigationWidth}px) {
    height: 195px;
  }
`;

const StyledContent = styled.div<{$isGlobalAdmin?: boolean}>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  grid-column-gap: 25px;
  padding: 6px 20px;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  line-height: 30px;
  font-weight: ${fontWeights.medium};

  &:hover {
    background: ${(p) => (p.$isGlobalAdmin ? p.theme.alphas.gray10 : 'none')};
  }
`;

const StyledContentHeader = styled(StyledContent)`
  grid-area: header;
  padding: 14px 20px 10px;
  height: 43px;

  color: ${(p) => p.theme.greys.shade60};
  line-height: 19px;
  font-weight: ${fontWeights.bold};
  letter-spacing: -0.4px;

  &:hover {
    background: transparent;
  }
`;

const StyledStatusDiv = styled.div<{$status: TeammateAccountStatusesEnum}>`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${(p) =>
    p.$status === TeammateAccountStatusesEnum.PENDING ? `color: ${p.theme.palette.orange.shade40}` : ''}
`;

const StyledFooter = styled.div`
  grid-area: footer;
  height: 70px;
  border-top: 1px solid ${(p) => p.theme.alphas.black30};
  display: flex;
  flex-direction: row-reverse;
  padding: 20px;
`;

const StyledShortcutActions = styled.div`
  display: flex;

  & > div {
    margin-right: 6px;
  }
`;

/*
 * Component.
 */

export const SettingsHomePendingTeammates: FC<SettingsHomePendingTeammatesProps> = (props) => (
  <StyledShortcutDiv>
    <IconWrapper>
      <SettingsHomeShortcutIcon icon={props.icon} />
    </IconWrapper>
    <StyledTitleWrapper>
      <StyledTextWrapper>
        <StyledShortcutTitle>{props.title}</StyledShortcutTitle>
      </StyledTextWrapper>
    </StyledTitleWrapper>
    <StyledContentHeader>
      <StyledOverflowTextWrapper>Name</StyledOverflowTextWrapper>
      <StyledOverflowTextWrapper>Status</StyledOverflowTextWrapper>
      <StyledOverflowTextWrapper>Invited</StyledOverflowTextWrapper>
    </StyledContentHeader>
    <StyledContentWrapper>
      {props.teammates.map(({teammate, onClick}) => (
        <StyledContent onClick={onClick} key={teammate.id} $isGlobalAdmin={props.isGlobalAdmin}>
          <StyledTeammateNameAndAvatar>
            <TeammateAvatar size={VisualSizesEnum.MEDIUM} teammate={teammate} />
            <StyledOverflowTextWrapper>
              {buildTeammateDisplayName(teammate) || teammate.email}
            </StyledOverflowTextWrapper>
          </StyledTeammateNameAndAvatar>
          <StyledStatusDiv $status={teammate.accountStatus}>
            {titleCase(teammate.accountStatus)}
          </StyledStatusDiv>
          <StyledOverflowTextWrapper>{formatDate(teammate.createdAt)}</StyledOverflowTextWrapper>
        </StyledContent>
      ))}
    </StyledContentWrapper>
    <StyledFooter>
      <StyledShortcutActions>
        <Button variant={ButtonVariantsEnum.LINK} isRounded onClick={props.onSecondaryClick}>
          {props.secondaryLabel}
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} isRounded onClick={props.onPrimaryClick}>
          {props.primaryLabel}
        </Button>
      </StyledShortcutActions>
    </StyledFooter>
  </StyledShortcutDiv>
);

function titleCase(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function formatDate(date: number) {
  if (!date) {
    return 'Active';
  }

  return DateTime.fromMillis(date).toRelative();
}
